/**
 * Advanced mode — a beta tester's private switch.
 *
 * Advanced mode (the floor planner + 3D designer as a 4th project type on
 * step 1, route /design) is shown to everyone only when the admin setting
 * `advancedDesignerEnabled` is on. Until then a tester turns it on for their
 * own browser with the hidden hot key Ctrl+Alt+Shift+A (AdvancedDesignerHotkey)
 * or by opening any page with `?advanced=1` (`?advanced=0` turns it off again).
 *
 * localStorage, so it survives a reload and a new tab: a tester flips it once.
 * Same shape as devUnlockResults.ts: one key, one event, a
 * useSyncExternalStore hook for the live value.
 */

import { useSyncExternalStore } from 'react';

const KEY = 'designer.advanced.beta';
const EVENT = 'advanced-designer-flag-changed';
const PARAM = 'advanced';

export function setAdvancedDesignerFlag(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    /* storage blocked — the event below still drives this tab's render */
  }
  window.dispatchEvent(new Event(EVENT));
}

// `?advanced=1` / `?advanced=0` is applied the first time the flag is read on
// a page load, so a deep link to /design?advanced=1 passes the route guard on
// its first render (no effect to wait for).
let urlApplied = false;
function applyUrlParam(): void {
  if (urlApplied || typeof window === 'undefined') return;
  urlApplied = true;
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    if (value === null) return;
    const on = value === '1' || value === 'true' || value === 'on';
    localStorage.setItem(KEY, on ? '1' : '');
    if (!on) localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export function isAdvancedDesignerFlagOn(): boolean {
  applyUrlParam();
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false; // private mode / blocked storage
  }
}

/** Flip the flag; returns the new value. */
export function toggleAdvancedDesignerFlag(): boolean {
  const next = !isAdvancedDesignerFlagOn();
  setAdvancedDesignerFlag(next);
  return next;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange); // flipped in another tab
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Live view of the flag. Re-renders the caller when it is toggled. */
export function useAdvancedDesignerFlag(): boolean {
  return useSyncExternalStore(subscribe, isAdvancedDesignerFlagOn, () => false);
}
